import * as React from "react";
import {Button, FormControlLabel, FormGroup, Grid, MenuItem, Switch, TextField} from "@material-ui/core";
import {API} from "aws-amplify";
import JsonRenderComponent from "../../../../utils/JsonRenderComponent";
import {Alert} from "@material-ui/lab";
import {LoadingSpinner} from "../../../../utils/LoadingSpinner";
import {getSubmitButtonProps} from "../../../../utils/SubmitButtonProps";
import {IRphDecisionApprovalRequest, rphDecisionApprovalDefault} from "./request/RphDecisionApprovalRequest";

interface IProps {
    initialProfileId?: string
    autoSubmit?: boolean
    // When set, the Submit button uses this background color (used to make it
    // stand out when embedded in the NewRx workflow). Standalone usage omits it.
    submitColor?: string
    // Called with the response after a successful submit (used by the NewRx
    // workflow to collapse this form into a completed summary container).
    onSuccess?: (response: any) => void
}

interface IState {
    errorMessage: string
    isJsonInput: boolean
    isLoading: boolean
    jsonInput: string
    request: IRphDecisionApprovalRequest
    response: any
}

enum InputField {
    PROFILE_ID,
    STATUS
}

class RphDecisionApprovalSection extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            errorMessage: '',
            isJsonInput: false,
            isLoading: false,
            jsonInput: rphDecisionApprovalDefault,
            request: {profileId: props.initialProfileId || '', status: 'APPROVED'} as IRphDecisionApprovalRequest,
            response: ''
        };

        this.toggleJsonInput = this.toggleJsonInput.bind(this);
        this.setRequest = this.setRequest.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleValidation = this.handleValidation.bind(this);
    }

    public componentDidMount() {
        if (this.props.autoSubmit && this.props.initialProfileId) {
            this.handleSubmit();
        }
    }

    public render() {
        return(
            <Grid
                container
                direction="column"
                justify="center"
                alignItems="center"
                spacing={3}
            >
                <Alert variant="filled" severity="info">
                    Please pull the CPCP customer for OTC Rx-less medication and provide profile id here.
                </Alert>

                <Grid item xs>
                    <FormGroup>
                        <FormControlLabel control={ <Switch onChange={ this.toggleJsonInput }/> } label="Toggle Json Input" />
                    </FormGroup>
                </Grid>


                { !this.state.isJsonInput &&
                    <Grid
                        container
                        direction="column"
                        justify="center"
                        alignItems="center"
                        spacing={3}
                    >
                        <Grid item xs>
                            <TextField
                                style={{width: 500}}
                                label="Profile Id (Required)"
                                variant="outlined"
                                value={ this.state.request.profileId || '' }
                                onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.PROFILE_ID, event) }
                            />
                        </Grid>
                        <Grid item xs>
                            <TextField
                                select
                                style={{width: 500}}
                                label="Status (Required)"
                                variant="outlined"
                                value={ this.state.request.status || ''}
                                onChange={ (event: React.ChangeEvent<HTMLTextAreaElement>) => this.setRequestFields(InputField.STATUS, event) }
                            >
                                <MenuItem value="APPROVED">APPROVED</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                }

                { this.state.isJsonInput &&
                    <Grid item xs>
                        <TextField
                            multiline
                            rows={ 8 }
                            label="Rph Decision Approval Request"
                            variant="filled"
                            value={ this.state.jsonInput }
                            onChange={ this.setRequest }
                            style={{ width: 800 }}
                        />
                    </Grid>
                }

                <Grid item xs>
                    <Button
                        {...getSubmitButtonProps(this.props.submitColor)}
                        onClick={ this.handleSubmit }
                    >
                        Submit
                    </Button>
                </Grid>

                { this.state.isLoading && <LoadingSpinner/> }

                { this.state.errorMessage &&
                    <Alert variant="filled" severity="info">
                        Error encountered: { this.state.errorMessage }
                    </Alert>
                }

                { this.state.response &&
                    <div>
                        <br/>
                        <h4>Status</h4>
                        <p style={{
                            border: '3px solid lightgray',
                            padding: '5px',
                            whiteSpace: "pre-wrap",
                            wordBreak: "normal",
                            wordWrap: "break-word"
                        }}>
                            <JsonRenderComponent jsonObject={ this.state.response } printHtml={ false }/>
                        </p>
                    </div>
                }
            </Grid>
        );
    }

    private toggleJsonInput() {
        this.setState({ isJsonInput: !this.state.isJsonInput });
    }

    private handleValidation() {
        let formIsValid = true;
        console.log("Profile id : ", this.state.request.profileId)
        console.log("Status : ", this.state.request.status)
        if (!this.state.request.profileId) {
            alert('Please enter the profile id');
            formIsValid = false
        } else if (!this.state.request.profileId.match("^amzn[1-9]+[0-9]*.([A-Za-z0-9]+.)+[A-Za-z0-9]*$")) {
            alert('Please enter the valid profile id.');
            formIsValid = false
        }
        if (!this.state.request.status) {
            alert('Please select a status');
            formIsValid = false
        }
        return formIsValid;
    }

    private setRequestFields(field: InputField, event: React.ChangeEvent<HTMLTextAreaElement>) {
        const updatedRequest: IRphDecisionApprovalRequest = this.state.request;

        switch (field) {
            case InputField.PROFILE_ID: {
                updatedRequest.profileId = event.target.value;
                this.setState({ request: updatedRequest });
                console.log(this.state.request);
                break;
            }
            case InputField.STATUS: {
                updatedRequest.status = event.target.value;
                this.setState({ request: updatedRequest });
                console.log(this.state.request);
                break;
            }
            default: {
                break;
            }
        }
    }

    private setRequest(event: React.ChangeEvent<HTMLTextAreaElement>) {
        this.setState({ jsonInput: event.target.value });
    }

    private handleSubmit() {
        if (this.state.isLoading) {
            return;
        }

        this.setState({ isLoading: true, errorMessage: '' });

        if (this.handleValidation()) {
            const config = {
                body: this.createRequestBody(),
                headers: {}
            }
            this.callRphDecisionService(config);
        } else {
            this.setState({ isLoading: false });
        }
    }

    private createRequestBody() {
        return {
            profileId: this.state.request.profileId,
            status: this.state.request.status
        };
    }

    private callRphDecisionService(config: any) {
        const apiName = 'ApaqPortalAPI';
        const path = '/pharmacy-customer/rph-decision';

        console.log(config)
        API.post(apiName, path, config).then((getResponse: any) => {
            console.log(getResponse);
            this.setState({ response: getResponse, errorMessage: '', isLoading: false });
            if (this.props.onSuccess) {
                this.props.onSuccess(getResponse);
            }
        }).catch((error) => {
            console.log(error);
            const errorMessage = error?.response?.data?.message || error.message || 'An unexpected error occurred';
            this.setState({ response: '', errorMessage, isLoading: false });
        });
    }
}

export default RphDecisionApprovalSection;
